import React, { useEffect, useState } from "react";
import { useI18n } from "@/contexts/LanguageContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";

const PHASES = ["free_launch", "promotional", "standard"];

export default function AdminBilling() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("free_launch");
  const [endsAt, setEndsAt] = useState("");
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [currency, setCurrency] = useState("eur");
  const [prices, setPrices] = useState({
    promotional: { monthly: 990, six_month: 4990, yearly: 8990 },
    standard: { monthly: 1590, six_month: 7990, yearly: 12990 },
  });
  const [giftEmail, setGiftEmail] = useState("");
  const [giftEndsAt, setGiftEndsAt] = useState("");
  const [giftStatus, setGiftStatus] = useState(null);
  const [giftSaving, setGiftSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/billing-config");
      setCfg(r.data);
      setPhase(r.data.phase || "free_launch");
      setEndsAt(r.data.free_launch_ends_at?.slice(0, 10) || "");
      setAutoAdvance(!!r.data.auto_advance_after_free_launch);
      setCurrency(r.data.currency || "eur");
      if (r.data.prices_cents) {
        setPrices({
          promotional: { ...prices.promotional, ...(r.data.prices_cents.promotional || {}) },
          standard: { ...prices.standard, ...(r.data.prices_cents.standard || {}) },
        });
      }
    } catch (e) {
      setError(extractError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        phase,
        free_launch_ends_at: endsAt
          ? new Date(`${endsAt}T00:00:00Z`).toISOString()
          : null,
        auto_advance_after_free_launch: autoAdvance,
        currency,
        prices_cents: prices,
      };
      await api.patch("/admin/billing-config", body);
      toast.success(t("app.admin.billing.saved"));
      await load();
    } catch (e) {
      toast.error(extractError(e, t("app.admin.billing.error")));
    } finally {
      setSaving(false);
    }
  };

  const updatePrice = (tier, plan, value) => {
    const int = parseInt(value, 10);
    setPrices((p) => ({
      ...p,
      [tier]: {
        ...p[tier],
        [plan]: Number.isFinite(int) && int >= 0 ? int : 0,
      },
    }));
  };

  const checkGift = async () => {
    if (!giftEmail.trim()) return;
    setGiftSaving(true);
    try {
      const r = await api.get(`/admin/complimentary-access?email=${encodeURIComponent(giftEmail.trim())}`);
      setGiftStatus(r.data);
      setGiftEndsAt(r.data.expires_at?.slice(0, 10) || "");
      toast.success(r.data.active ? "Accesso gratuito attivo" : "Nessun omaggio attivo");
    } catch (e) {
      setGiftStatus(null);
      toast.error(extractError(e, "Utente non trovato"));
    } finally {
      setGiftSaving(false);
    }
  };

  const saveGift = async () => {
    if (!giftEmail.trim()) return;
    setGiftSaving(true);
    try {
      await api.post("/admin/complimentary-access", {
        email: giftEmail.trim(),
        expires_at: giftEndsAt ? new Date(`${giftEndsAt}T23:59:59Z`).toISOString() : null,
      });
      toast.success(giftEndsAt ? "Accesso gratuito assegnato" : "Accesso gratuito senza scadenza assegnato");
      await checkGift();
    } catch (e) {
      toast.error(extractError(e, "Impossibile assegnare l’accesso"));
    } finally {
      setGiftSaving(false);
    }
  };

  const revokeGift = async () => {
    setGiftSaving(true);
    try {
      await api.delete(`/admin/complimentary-access/${encodeURIComponent(giftEmail.trim())}`);
      setGiftStatus({ active: false });
      setGiftEndsAt("");
      toast.success("Accesso gratuito revocato");
    } catch (e) {
      toast.error(extractError(e, "Impossibile revocare l’accesso"));
    } finally {
      setGiftSaving(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-billing-page">
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {t("app.admin.nav.billing")}
      </p>
      <h1 className="mt-2 text-3xl md:text-4xl">{t("app.admin.billing.title")}</h1>
      <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {t("app.admin.billing.subtitle")}
      </p>

      {error ? (
        <div className="mt-6 text-sm" style={{ color: "hsl(var(--destructive))" }}>
          {error}
        </div>
      ) : null}

      <div className="mt-8 nvl-card p-6 space-y-6" data-testid="admin-billing-form">
        <div>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.billing.phaseLabel")}
          </label>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {PHASES.map((p) => {
              const active = phase === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPhase(p)}
                  data-testid={`admin-billing-phase-${p}`}
                  className="px-4 py-3 rounded-md border text-sm text-left"
                  style={{
                    borderColor: active ? "hsl(var(--nvl-accent))" : "hsl(var(--nvl-border))",
                    backgroundColor: active ? "hsl(var(--nvl-accent) / 0.08)" : "transparent",
                  }}
                >
                  <div className="font-medium">
                    {t(`app.admin.billing.phase.${p}`)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest"
                   style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.billing.freeLaunchEndsAt")}
            </label>
            <input
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="nvl-input mt-2"
              data-testid="admin-billing-ends-at"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest"
                   style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.billing.currency")}
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="nvl-input mt-2"
              data-testid="admin-billing-currency"
            >
              <option value="eur">EUR</option>
              <option value="usd">USD</option>
              <option value="gbp">GBP</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoAdvance}
            onChange={(e) => setAutoAdvance(e.target.checked)}
            data-testid="admin-billing-auto-advance"
          />
          {t("app.admin.billing.autoAdvance")}
        </label>

        <div>
          <div className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.billing.pricesTitle")}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {["promotional", "standard"].map((tier) => (
              <div key={tier} className="border rounded-md p-4"
                   style={{ borderColor: "hsl(var(--nvl-border))" }}
                   data-testid={`admin-billing-tier-${tier}`}>
                <div className="font-medium mb-3">
                  {t(`app.admin.billing.${tier}`)}
                </div>
                {["monthly", "six_month", "yearly"].map((plan) => (
                  <div key={plan} className="flex items-center gap-3 mb-2">
                    <div className="w-24 text-sm"
                         style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                      {t(`app.admin.billing.${plan === "six_month" ? "sixMonth" : plan}`)}
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={prices[tier][plan] ?? 0}
                      onChange={(e) => updatePrice(tier, plan, e.target.value)}
                      className="nvl-input flex-1"
                      data-testid={`admin-billing-price-${tier}-${plan}`}
                    />
                    <div className="text-xs w-16 text-right"
                         style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                      {((prices[tier][plan] ?? 0) / 100).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="nvl-btn nvl-btn-primary disabled:opacity-50"
            data-testid="admin-billing-save"
          >
            {saving ? t("common.loading") : t("app.admin.billing.save")}
          </button>
        </div>
      </div>

      <div className="mt-8 nvl-card p-6 space-y-5" data-testid="admin-complimentary-access">
        <div>
          <div className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
            Accesso omaggio
          </div>
          <h2 className="mt-2 text-xl">Regala l’accesso a un utente</h2>
          <p className="mt-1 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            L’accesso omaggio non richiede carta, può avere una scadenza oppure restare attivo finché non lo revochi.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>Email utente</label>
            <input type="email" value={giftEmail} onChange={(e) => { setGiftEmail(e.target.value); setGiftStatus(null); }} className="nvl-input mt-2" placeholder="nome@esempio.it" data-testid="admin-gift-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>Scadenza facoltativa</label>
            <input type="date" value={giftEndsAt} onChange={(e) => setGiftEndsAt(e.target.value)} className="nvl-input mt-2" data-testid="admin-gift-expires-at" />
          </div>
        </div>
        {giftStatus ? <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {giftStatus.active ? `Accesso omaggio attivo${giftStatus.expires_at ? ` fino al ${new Date(giftStatus.expires_at).toLocaleDateString("it-IT")}` : " senza scadenza"}.` : "Nessun accesso omaggio attivo per questo utente."}
        </div> : null}
        <div className="flex flex-wrap gap-3">
          <button onClick={checkGift} disabled={giftSaving || !giftEmail.trim()} className="nvl-btn disabled:opacity-50" data-testid="admin-gift-check">Verifica</button>
          <button onClick={saveGift} disabled={giftSaving || !giftEmail.trim()} className="nvl-btn nvl-btn-primary disabled:opacity-50" data-testid="admin-gift-save">Assegna omaggio</button>
          {giftStatus?.active ? <button onClick={revokeGift} disabled={giftSaving} className="nvl-btn disabled:opacity-50" data-testid="admin-gift-revoke">Revoca omaggio</button> : null}
        </div>
      </div>
    </div>
  );
}
